import React, { useRef, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { 
  generatePDFFromElement 
} from "../../../utils/invoiceUtils";

const ViewParentEngagementGModal = ({ 
  isOpen, 
  onClose, 
  sectionId, 
  sectionName, 
  className,
  campusName
}) => {
  const reportRef = useRef();
  const [loading, setLoading] = useState(false);

  // Dummy data for group engagement report
  const dummyGroupReport = {
    sectionInfo: {
      campusName,
      className,
      sectionName,
      reportDate: new Date().toLocaleDateString('en-GB')
    },
    summary: {
      totalParents: 45,
      totalKids: 48, // Some parents might have multiple kids
      totalSurveysSubmitted: 186,
      totalComplaintsRegistered: 12,
      notificationCheckPercentage: "84%",
      activeParentsPercentage: "78%"
    },
    surveyBreakup: [
      { type: "Academic Satisfaction", submitted: 42, available: 45, percentage: "93%" },
      { type: "School Facilities", submitted: 38, available: 45, percentage: "84%" },
      { type: "Teacher Performance", submitted: 35, available: 45, percentage: "78%" },
      { type: "Extracurricular Activities", submitted: 32, available: 45, percentage: "71%" },
      { type: "General Feedback", submitted: 39, available: 45, percentage: "87%" }
    ],
    complaintBreakup: [
      { type: "Academic", count: 5, percentage: "42%" },
      { type: "Administrative", count: 3, percentage: "25%" },
      { type: "Technical", count: 2, percentage: "17%" },
      { type: "Infrastructure", count: 2, percentage: "17%" }
    ],
    notificationCategories: [
      { type: "Academic Updates", sent: 1980, checked: 1742, percentage: "88%" },
      { type: "Events & Activities", sent: 1710, checked: 1368, percentage: "80%" },
      { type: "Fee Reminders", sent: 1125, checked: 1080, percentage: "96%" },
      { type: "General Announcements", sent: 2160, checked: 1728, percentage: "80%" },
      { type: "Emergency Alerts", sent: 225, checked: 218, percentage: "97%" }
    ],
    parentActivity: {
      daily: { count: 12, percentage: "27%" },
      weekly: { count: 18, percentage: "40%" },
      monthly: { count: 15, percentage: "33%" },
      inactive: { count: 10, percentage: "22%" }
    },
    engagementLevels: {
      high: { count: 18, percentage: "40%" }, // 80-100
      medium: { count: 16, percentage: "36%" }, // 60-79
      low: { count: 11, percentage: "24%" } // Below 60
    },
    monthlyTrends: [
      { month: "September", surveys: 38, complaints: 2, activeParents: 41 },
      { month: "October", surveys: 42, complaints: 3, activeParents: 39 },
      { month: "November", surveys: 45, complaints: 1, activeParents: 43 },
      { month: "December", surveys: 39, complaints: 4, activeParents: 37 },
      { month: "January", surveys: 22, complaints: 2, activeParents: 35 }
    ]
  };

  // Simulate loading for better UX
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [isOpen]);

  const downloadPDF = () => {
    generatePDFFromElement(reportRef, "Section_Parent_Engagement_Group_Report.pdf");
  };

  const downloadExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    // Summary Sheet
    const summaryData = [
      ["Parent Engagement Group Report"],
      ["Campus", dummyGroupReport.sectionInfo.campusName],
      ["Class", dummyGroupReport.sectionInfo.className],
      ["Section", dummyGroupReport.sectionInfo.sectionName],
      ["Report Date", dummyGroupReport.sectionInfo.reportDate],
      [],
      ["Summary Metrics", ""],
      ["Total Parents", dummyGroupReport.summary.totalParents],
      ["Total Kids", dummyGroupReport.summary.totalKids],
      ["Total Surveys Submitted", dummyGroupReport.summary.totalSurveysSubmitted],
      ["Total Complaints Registered", dummyGroupReport.summary.totalComplaintsRegistered],
      ["Notification Check %", dummyGroupReport.summary.notificationCheckPercentage],
      ["Active Parents %", dummyGroupReport.summary.activeParentsPercentage],
      [],
      ["Survey Breakdown", "", "", ""],
      ["Survey Type", "Submitted", "Available", "Percentage"],
      ...dummyGroupReport.surveyBreakup.map(survey => [survey.type, survey.submitted, survey.available, survey.percentage]),
      [],
      ["Complaint Breakdown", "", ""],
      ["Complaint Type", "Count", "Percentage"],
      ...dummyGroupReport.complaintBreakup.map(complaint => [complaint.type, complaint.count, complaint.percentage]),
      [],
      ["Parent Activity Level", "", ""],
      ["Activity Level", "Count", "Percentage"],
      ["Daily Active", dummyGroupReport.parentActivity.daily.count, dummyGroupReport.parentActivity.daily.percentage],
      ["Weekly Active", dummyGroupReport.parentActivity.weekly.count, dummyGroupReport.parentActivity.weekly.percentage],
      ["Monthly Active", dummyGroupReport.parentActivity.monthly.count, dummyGroupReport.parentActivity.monthly.percentage],
      ["Inactive", dummyGroupReport.parentActivity.inactive.count, dummyGroupReport.parentActivity.inactive.percentage]
    ];
    
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
    
    // Notification Details Sheet
    const notificationData = [
      ["Notification Category Breakdown"],
      ["Category", "Sent", "Checked", "Check Rate"],
      ...dummyGroupReport.notificationCategories.map(notif => [
        notif.type, notif.sent, notif.checked, notif.percentage
      ])
    ];
    
    const notificationSheet = XLSX.utils.aoa_to_sheet(notificationData);
    XLSX.utils.book_append_sheet(workbook, notificationSheet, "Notifications");
    
    XLSX.writeFile(workbook, "Section_Parent_Engagement_Group_Report.xlsx");
  };

  const getActivityColor = (level) => {
    switch (level) {
      case 'daily': return 'text-green-600 bg-green-50';
      case 'weekly': return 'text-blue-600 bg-blue-50';
      case 'monthly': return 'text-yellow-600 bg-yellow-50';
      case 'inactive': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div ref={reportRef} className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-h-[90vh] overflow-y-auto relative border border-gray-300">
        {loading && <p>Loading report...</p>}

        {/* Report Header */}
        <div className="text-center mb-6 border-b pb-4">
          <h1 className="text-2xl font-bold text-gray-800">{campusName}</h1>
          <h2 className="text-xl font-semibold mt-2">Parent Engagement Group Report</h2>
          <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
            <div><strong>Class:</strong> {className}</div>
            <div><strong>Section:</strong> {sectionName}</div>
            <div><strong>Generated On:</strong> {new Date().toLocaleDateString('en-GB')}</div>
          </div>
        </div>

        {/* Key Metrics Overview */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Key Metrics Overview</h3>
          <div className="grid grid-cols-3 gap-6">
            <div className="bg-blue-50 p-6 rounded-lg text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {dummyGroupReport.summary.totalParents}
              </div>
              <div className="text-sm text-gray-600 mb-2">Total Parents</div>
              <div className="text-lg font-semibold text-blue-800">
                {dummyGroupReport.summary.totalKids} Kids
              </div>
            </div>
            <div className="bg-green-50 p-6 rounded-lg text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">
                {dummyGroupReport.summary.totalSurveysSubmitted}
              </div>
              <div className="text-sm text-gray-600 mb-2">Total Surveys</div>
              <div className="text-sm text-green-700">Submitted</div>
            </div>
            <div className="bg-purple-50 p-6 rounded-lg text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">
                {dummyGroupReport.summary.totalComplaintsRegistered}
              </div>
              <div className="text-sm text-gray-600 mb-2">Total Complaints</div>
              <div className="text-sm text-purple-700">Registered</div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-6 mt-6">
            <div className="bg-yellow-50 p-6 rounded-lg text-center">
              <div className="text-3xl font-bold text-yellow-600 mb-2">
                {dummyGroupReport.summary.notificationCheckPercentage}
              </div>
              <div className="text-sm text-gray-600">Notifications Checked</div>
            </div>
            <div className="bg-indigo-50 p-6 rounded-lg text-center">
              <div className="text-3xl font-bold text-indigo-600 mb-2">
                {dummyGroupReport.summary.activeParentsPercentage}
              </div>
              <div className="text-sm text-gray-600">Active Parents</div>
            </div>
          </div>
        </div>

        {/* Survey Participation Breakdown */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Survey Participation Breakdown</h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-2 text-left">Survey Type</th>
                  <th className="border border-gray-300 px-4 py-2 text-center">Submitted</th>
                  <th className="border border-gray-300 px-4 py-2 text-center">Available</th>
                  <th className="border border-gray-300 px-4 py-2 text-center">Participation Rate</th>
                </tr>
              </thead>
              <tbody>
                {dummyGroupReport.surveyBreakup.map((survey, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2">{survey.type}</td>
                    <td className="border border-gray-300 px-4 py-2 text-center font-semibold">
                      {survey.submitted}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      {survey.available}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">
                        {survey.percentage}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-semibold">
                  <td className="border border-gray-300 px-4 py-2">Total</td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    {dummyGroupReport.summary.totalSurveysSubmitted}
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    {dummyGroupReport.surveyBreakup.reduce((sum, survey) => sum + survey.available, 0)}
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm font-medium">
                      {Math.round((dummyGroupReport.summary.totalSurveysSubmitted / dummyGroupReport.surveyBreakup.reduce((sum, survey) => sum + survey.available, 0)) * 100)}%
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Complaints Breakdown */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Complaints Registered Breakdown</h3>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-3">Complaint Categories</h4>
              <div className="space-y-3">
                {dummyGroupReport.complaintBreakup.map((complaint, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <span className="text-gray-700">{complaint.type}</span>
                    <div className="flex items-center space-x-2">
                      <span className="font-semibold">{complaint.count}</span>
                      <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">
                        {complaint.percentage}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-3">Complaint Summary</h4>
              <div className="bg-red-50 p-6 rounded-lg text-center">
                <div className="text-4xl font-bold text-red-600 mb-2">
                  {dummyGroupReport.summary.totalComplaintsRegistered}
                </div>
                <div className="text-sm text-gray-600 mb-2">Total Complaints</div>
                <div className="text-xs text-red-700">
                  Average: {(dummyGroupReport.summary.totalComplaintsRegistered / dummyGroupReport.summary.totalParents).toFixed(1)} per parent
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notification Engagement */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Notification Engagement by Category</h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-2 text-left">Notification Type</th>
                  <th className="border border-gray-300 px-4 py-2 text-center">Sent</th>
                  <th className="border border-gray-300 px-4 py-2 text-center">Checked</th>
                  <th className="border border-gray-300 px-4 py-2 text-center">Check Rate</th>
                </tr>
              </thead>
              <tbody>
                {dummyGroupReport.notificationCategories.map((notification, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2">{notification.type}</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      {notification.sent.toLocaleString()}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center font-semibold">
                      {notification.checked.toLocaleString()}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm font-medium">
                        {notification.percentage}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-semibold">
                  <td className="border border-gray-300 px-4 py-2">Overall Average</td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    {dummyGroupReport.notificationCategories.reduce((sum, notif) => sum + notif.sent, 0).toLocaleString()}
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    {dummyGroupReport.notificationCategories.reduce((sum, notif) => sum + notif.checked, 0).toLocaleString()}
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">
                      {dummyGroupReport.summary.notificationCheckPercentage}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Parent Activity Levels */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Parent Activity Levels</h3>
          <div className="grid grid-cols-4 gap-4">
            <div className={`p-4 rounded-lg text-center ${getActivityColor('daily')}`}>
              <div className="text-2xl font-bold mb-2">
                {dummyGroupReport.parentActivity.daily.count}
              </div>
              <div className="text-sm mb-1">Daily Active</div>
              <div className="text-xs font-medium">
                {dummyGroupReport.parentActivity.daily.percentage}
              </div>
            </div>
            <div className={`p-4 rounded-lg text-center ${getActivityColor('weekly')}`}>
              <div className="text-2xl font-bold mb-2">
                {dummyGroupReport.parentActivity.weekly.count}
              </div>
              <div className="text-sm mb-1">Weekly Active</div>
              <div className="text-xs font-medium">
                {dummyGroupReport.parentActivity.weekly.percentage}
              </div>
            </div>
            <div className={`p-4 rounded-lg text-center ${getActivityColor('monthly')}`}>
              <div className="text-2xl font-bold mb-2">
                {dummyGroupReport.parentActivity.monthly.count}
              </div>
              <div className="text-sm mb-1">Monthly Active</div>
              <div className="text-xs font-medium">
                {dummyGroupReport.parentActivity.monthly.percentage}
              </div>
            </div>
            <div className={`p-4 rounded-lg text-center ${getActivityColor('inactive')}`}>
              <div className="text-2xl font-bold mb-2">
                {dummyGroupReport.parentActivity.inactive.count}
              </div>
              <div className="text-sm mb-1">Inactive</div>
              <div className="text-xs font-medium">
                {dummyGroupReport.parentActivity.inactive.percentage}
              </div>
            </div>
          </div>
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium mb-2">Activity Definitions:</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><strong>Daily Active:</strong> Logged in at least once daily</div>
              <div><strong>Weekly Active:</strong> Logged in at least once per week</div>
              <div><strong>Monthly Active:</strong> Logged in at least once per month</div>
              <div><strong>Inactive:</strong> No login activity in the past month</div>
            </div>
          </div>
        </div>

        {/* Engagement Distribution */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Parent Engagement Distribution</h3>
          <div className="grid grid-cols-3 gap-6">
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600 mb-2">
                {dummyGroupReport.engagementLevels.high.count}
              </div>
              <div className="text-sm text-gray-600 mb-1">High Engagement</div>
              <div className="text-xs text-green-700">{dummyGroupReport.engagementLevels.high.percentage}</div>
              <div className="text-xs text-gray-500 mt-1">(80-100 score)</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-yellow-600 mb-2">
                {dummyGroupReport.engagementLevels.medium.count}
              </div>
              <div className="text-sm text-gray-600 mb-1">Medium Engagement</div>
              <div className="text-xs text-yellow-700">{dummyGroupReport.engagementLevels.medium.percentage}</div>
              <div className="text-xs text-gray-500 mt-1">(60-79 score)</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-red-600 mb-2">
                {dummyGroupReport.engagementLevels.low.count}
              </div>
              <div className="text-sm text-gray-600 mb-1">Low Engagement</div>
              <div className="text-xs text-red-700">{dummyGroupReport.engagementLevels.low.percentage}</div>
              <div className="text-xs text-gray-500 mt-1">(Below 60 score)</div>
            </div>
          </div>
        </div>

        {/* Monthly Trends */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">5-Month Trend Analysis</h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-2 text-left">Month</th>
                  <th className="border border-gray-300 px-4 py-2 text-center">Surveys Submitted</th>
                  <th className="border border-gray-300 px-4 py-2 text-center">Complaints Registered</th>
                  <th className="border border-gray-300 px-4 py-2 text-center">Active Parents</th>
                </tr>
              </thead>
              <tbody>
                {dummyGroupReport.monthlyTrends.map((trend, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2 font-medium">{trend.month}</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">{trend.surveys}</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">{trend.complaints}</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">{trend.activeParents}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Download Buttons */}
        <div className="no-print flex justify-end mt-6 space-x-4 border-t pt-4">
          <button onClick={downloadPDF} className="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded text-white transition-colors">
            Download as PDF
          </button>
          <button onClick={downloadExcel} className="bg-green-500 hover:bg-green-600 px-4 py-2 rounded text-white transition-colors">
            Download as Excel
          </button>
          <button onClick={onClose} className="bg-gray-400 hover:bg-gray-500 px-4 py-2 rounded text-white transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewParentEngagementGModal;